import React, {useEffect} from 'react';
  import {useApi} from "../../../providers/ApiProvider";
  import BusinessesModel from "../../../models/BusinessesModel";
  import {useParams} from "react-router-dom";
  import {useBreadcrumbs} from "../../../providers/BreadcrumbsProvider";
  import BusinessViewHeader from "./../BusinessViewHeader";
  import BusinessViewMenu from "../BusinessViewMenu";
  import {useTranslation} from "react-i18next";
  import {useTranslation as useMyTranslation} from "../../../providers/TranslationProvider";

  function BusinessesReviewLink() {
      const {language} = useMyTranslation();
      const {t} = useTranslation();
      const params = useParams();
      const apiContext = useApi();
      const businessesModel = new BusinessesModel(apiContext.api);
      const breadcrumbs = useBreadcrumbs();

      const [loading, setLoading] = React.useState(true);
      const [linkData, setLinkData] = React.useState(null);
      const [copied, setCopied] = React.useState(false);

      const setMetaData = (profile) => {
          if (profile !== false) {
              breadcrumbs.update({
                  title: profile.title,
                  breadcrumbs: [
                      {label: t('Businesses List'), url: '/businesses'},
                      {label: profile.title, url: '/businesses/' + params.locationId},
                      {label: t('Bewertungslink & QR-Code')}
                  ]
              });
              document.title = t('Bewertungslink & QR-Code') + ' | ' + profile.title + ' | LocalBoost';
          }
      }

      const onProfileRefresh = (profile) => {
          setMetaData(profile);
      }

      const loadReviewLink = () => {
          setLoading(true);
          businessesModel.getReviewLink(params.locationId).then((response) => {
              if (response !== false) {
                  setLinkData(response);
              }
          }).finally(() => {
              setLoading(false);
          });
      }

      const copyToClipboard = (text) => {
          navigator.clipboard.writeText(text).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
          });
      }

      const downloadQRCode = () => {
          if (!linkData) return;
          const link = document.createElement('a');
          link.href = linkData.qrCodeUrl;
          link.download = 'review-qr-code-' + (linkData.businessName || 'business').replace(/[^a-zA-Z0-9]/g, '-') + '.png';
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
      }

      useEffect(() => {
          businessesModel.view(params.locationId).then((response) => {
              if (response !== false) {
                  setMetaData(response.data);
              }
          });
          loadReviewLink();
      }, [language]);

      return (
          <>
              <BusinessViewHeader locationId={params.locationId} onProfileRefresh={onProfileRefresh} />
              <BusinessViewMenu locationId={params.locationId} active="reviews" />

              {loading && (
                  <div className="card rounded-2 mt-3">
                      <div className="card-body p-5 text-center">
                          <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Loading...</span>
                          </div>
                      </div>
                  </div>
              )}

              {!loading && linkData && (
                  <>
                      <div className="row mt-3">
                          <div className="col-lg-7">
                              <div className="card rounded-2 h-100">
                                  <div className="card-body p-4">
                                      <h5 className="fw-semibold mb-4">
                                          <i className="ti ti-link me-2 text-primary"></i>
                                          {t('Direkter Bewertungslink')}
                                      </h5>

                                      <p className="text-muted mb-3">
                                          {t('Mit diesem Link k\u00f6nnen Ihre Kunden direkt eine Google-Bewertung f\u00fcr Ihr Unternehmen schreiben. Teilen Sie diesen Link per E-Mail, SMS, WhatsApp oder auf Ihren sozialen Medien.')}
                                      </p>

                                      {linkData.reviewUrl ? (
                                          <>
                                              <div className="input-group mb-3">
                                                  <input type="text" className="form-control bg-light" value={linkData.reviewUrl} readOnly />
                                                  <button className={"btn " + (copied ? "btn-success" : "btn-primary")} onClick={() => copyToClipboard(linkData.reviewUrl)}>
                                                      <i className={"ti " + (copied ? "ti-check" : "ti-copy") + " me-1"}></i>
                                                      {copied ? t('Kopiert!') : t('Kopieren')}
                                                  </button>
                                              </div>

                                              <div className="row g-2 mb-4">
                                                  <div className="col-auto">
                                                      <a href={"mailto:?subject=" + encodeURIComponent(t('Bewerten Sie uns!')) + "&body=" + encodeURIComponent(t('Wir w\u00fcrden uns \u00fcber Ihre Bewertung freuen: ') + linkData.reviewUrl)} className="btn btn-outline-primary btn-sm">
                                                          <i className="ti ti-mail me-1"></i> {t('Per E-Mail teilen')}
                                                      </a>
                                                  </div>
                                                  <div className="col-auto">
                                                      <a href={"https://wa.me/?text=" + encodeURIComponent(t('Bewerten Sie uns auf Google: ') + linkData.reviewUrl)} target="_blank" rel="noopener noreferrer" className="btn btn-outline-success btn-sm">
                                                          <i className="ti ti-brand-whatsapp me-1"></i> WhatsApp
                                                      </a>
                                                  </div>
                                                  <div className="col-auto">
                                                      <a href={linkData.reviewUrl} target="_blank" rel="noopener noreferrer" className="btn btn-outline-secondary btn-sm">
                                                          <i className="ti ti-external-link me-1"></i> {t('Link testen')}
                                                      </a>
                                                  </div>
                                              </div>
                                          </>
                                      ) : (
                                          <div className="alert alert-warning">
                                              <i className="ti ti-alert-triangle me-2"></i>
                                              {t('Es konnte kein Bewertungslink generiert werden. Stellen Sie sicher, dass Ihr Google Business Profil korrekt verbunden ist.')}
                                          </div>
                                      )}

                                      <h6 className="fw-semibold mt-4 mb-3">
                                          <i className="ti ti-bulb me-2 text-warning"></i>
                                          {t('Tipps zur Verwendung')}
                                      </h6>
                                      <ul className="mb-0">
                                          <li className="mb-2">{t('F\u00fcgen Sie den Link in Ihre E-Mail-Signatur ein')}</li>
                                          <li className="mb-2">{t('Drucken Sie den QR-Code auf Visitenkarten, Flyer oder Rechnungen')}</li>
                                          <li className="mb-2">{t('Senden Sie den Link nach einem erfolgreichen Gesch\u00e4ftsabschluss per SMS oder WhatsApp')}</li>
                                          <li className="mb-2">{t('Platzieren Sie den QR-Code an der Kasse oder im Eingangsbereich')}</li>
                                          <li>{t('Teilen Sie den Link in Ihren Social-Media-Posts')}</li>
                                      </ul>
                                  </div>
                              </div>
                          </div>

                          <div className="col-lg-5">
                              <div className="card rounded-2 h-100">
                                  <div className="card-body p-4 text-center">
                                      <h5 className="fw-semibold mb-4">
                                          <i className="ti ti-qrcode me-2 text-primary"></i>
                                          {t('QR-Code')}
                                      </h5>

                                      {linkData.reviewUrl ? (
                                          <>
                                              <div className="bg-white p-4 rounded-3 d-inline-block mb-3" style={{border: '2px dashed #dee2e6'}}>
                                                  <img src={linkData.qrCodeUrl} alt="QR Code" className="img-fluid" style={{maxWidth: '300px'}} />
                                              </div>

                                              <p className="text-muted fs-3 mb-3">
                                                  {linkData.businessName && <strong>{linkData.businessName}</strong>}
                                              </p>

                                              <div className="d-flex justify-content-center gap-2 flex-wrap">
                                                  <button className="btn btn-primary" onClick={downloadQRCode}>
                                                      <i className="ti ti-download me-1"></i> {t('QR-Code herunterladen')}
                                                  </button>
                                                  <button className="btn btn-outline-primary" onClick={() => {
                                                      const printWindow = window.open('', '_blank');
                                                      printWindow.document.write('<html><head><title>QR Code - ' + (linkData.businessName || '') + '</title></head><body style="text-align:center;padding:40px;font-family:Arial,sans-serif;">');
                                                      printWindow.document.write('<h2>' + (linkData.businessName || '') + '</h2>');
                                                      printWindow.document.write('<p>' + t('Bewerten Sie uns auf Google!') + '</p>');
                                                      printWindow.document.write('<img src="' + linkData.qrCodeUrl + '" style="max-width:400px;" />');
                                                      printWindow.document.write('<p style="margin-top:20px;color:#666;font-size:12px;">' + linkData.reviewUrl + '</p>');
                                                      printWindow.document.write('</body></html>');
                                                      printWindow.document.close();
                                                      printWindow.focus();
                                                      printWindow.print();
                                                  }}>
                                                      <i className="ti ti-printer me-1"></i> {t('Drucken')}
                                                  </button>
                                              </div>

                                              <div className="mt-4 p-3 bg-light rounded-3 text-start">
                                                  <h6 className="fw-semibold mb-2 fs-3">
                                                      <i className="ti ti-info-circle me-1"></i> {t('QR-Code Formate')}
                                                  </h6>
                                                  <div className="d-flex gap-3 flex-wrap">
                                                      <a href={linkData.qrCodeUrl} target="_blank" rel="noopener noreferrer" className="text-primary fs-3">
                                                          <i className="ti ti-photo me-1"></i> 400x400px (PNG)
                                                      </a>
                                                      <a href={linkData.qrCodeUrlSmall} target="_blank" rel="noopener noreferrer" className="text-primary fs-3">
                                                          <i className="ti ti-photo me-1"></i> 200x200px (PNG)
                                                      </a>
                                                  </div>
                                              </div>
                                          </>
                                      ) : (
                                          <div className="py-4 text-muted">
                                              <i className="ti ti-qrcode-off fs-1 d-block mb-3"></i>
                                              <p>{t('QR-Code nicht verf\u00fcgbar')}</p>
                                          </div>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </div>

                      {linkData.placeId && (
                          <div className="card rounded-2 mt-3">
                              <div className="card-body p-4">
                                  <h6 className="fw-semibold mb-3"><i className="ti ti-info-circle me-2"></i>{t('Technische Details')}</h6>
                                  <div className="table-responsive">
                                      <table className="table table-sm mb-0">
                                          <tbody>
                                              <tr><td className="text-muted fw-semibold" style={{width: '200px'}}>Place ID</td><td><code>{linkData.placeId}</code></td></tr>
                                              <tr><td className="text-muted fw-semibold">{t('Bewertungslink')}</td><td><a href={linkData.reviewUrl} target="_blank" rel="noopener noreferrer">{linkData.reviewUrl}</a></td></tr>
                                              {linkData.mapsUrl && <tr><td className="text-muted fw-semibold">Google Maps</td><td><a href={linkData.mapsUrl} target="_blank" rel="noopener noreferrer">{linkData.mapsUrl}</a></td></tr>}
                                          </tbody>
                                      </table>
                                  </div>
                              </div>
                          </div>
                      )}
                  </>
              )}

              {!loading && !linkData && (
                  <div className="card rounded-2 mt-3">
                      <div className="card-body p-5 text-center text-muted">
                          <i className="ti ti-link-off fs-1 d-block mb-3"></i>
                          <h5>{t('Bewertungslink konnte nicht geladen werden')}</h5>
                          <p>{t('Bitte stellen Sie sicher, dass Ihr Google Business Profil korrekt verbunden ist.')}</p>
                      </div>
                  </div>
              )}
          </>
      );
  }

  export default BusinessesReviewLink;
  